//REACT
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

// ASSETS
import {
  default as defib,
  default as defibOff,
} from "assets/img/map/marker.defib.svg";

// STYLES
import {
  InterestPointsMenu,
  MapContainer,
  PreloaderWrapper,
} from "./MapStyled";

// LIBRARIES
import mapboxgl, { Map } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Supercluster from "supercluster";

//MISC
import { ButtonType } from "components/Atoms/ButtonAtom/ButtonAtomModel";
import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import { ModalType } from "components/Atoms/ResponsiveModal/ResponsiveModalModel";
import { COLORS, DEVICE_TYPE, SIZE } from "constants/variables";
import modRewrite from "locales/modRewrite";
import mapService from "services/mapService";
import {
  createCustomMarkerElement,
  getBounds,
  IpCategory,
  IpCategorySet,
  IpPoint,
  mapper,
} from "./MapModel";

// REDUX

// COMPONENTS
import ButtonAtom from "components/Atoms/ButtonAtom/ButtonAtom";
import CustomText from "components/Atoms/CustomText/CustomText";
import Preloader from "components/Atoms/Lotties/Preloader";
import MapDropdownAtom from "components/Atoms/MapDropDownAtom/MapDropdownAtom";
import { MapDropdownWrapper } from "components/Atoms/MapDropDownAtom/MapDropdownAtomStyled";
import ResponsiveModal from "components/Atoms/ResponsiveModal/ResponsiveModal";
import { CheckProps } from "components/Atoms/SelectInputFilter/SelectInputFilterModel";
import EventsGirdle from "components/EventsGirdle/EventsGirdle";
import newsAndMediaService from "services/newsAndMediaService";
import formatDate from "utils/formatDate";

mapboxgl.accessToken =
  "pk.eyJ1Ijoib3Jzb25hcGlrZXkiLCJhIjoiY2w4Y3Y4YXFrMTVmYzNxcGJnNnRpNXRvdCJ9.RaCSdr1r6sz6NpyL8vFwqA";

const MapComponent: React.FC = () => {
  // PROPERTIES
  const location = useLocation();
  const { rgxId } = useParams();
  let paramCategoryId = 10;
  if (rgxId !== undefined) {
    const tmpIds: any = rgxId.split("-");
    paramCategoryId = parseInt(tmpIds.pop());
  } else {
    paramCategoryId = location?.state?.id || 10;
  }

  // LIBRARY CONSTANTS
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: DEVICE_TYPE.mobileHeader });
  const navigate = useNavigate();

  // STATE CONSTANTS
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const years = Array.from({ length: 50 }, (_, i) => 2000 + i);
  const currentYear = new Date().getFullYear();
  const currentYearIndexDefault = years.indexOf(currentYear);
  const [year, setYear] = useState<number>(currentYearIndexDefault);
  const [month, setMonth] = useState(currentMonth);
  const [searchTerm, setSearchTerm] = useState("");
  const [checkedState, setCheckedState] = useState<CheckProps>({
    allEvents: true,
    dsuEvents: false,
    partnerEvents: false,
  });

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<Map | null>(null);
  const [ipPoints, setIpPoints] = useState<IpCategorySet>({});
  const [ipCategories, setIpCategories] = useState<IpCategory[]>([]);
  const [pinInfo, setPinInfo] = useState<any | null>(null);
  const [selectedIpCategory, setSelectedIpCategory] =
    useState<number>(paramCategoryId);
  //eslint-disable-next-line
  const [shownMarkers, setShownMarkers] = useState<mapboxgl.Marker[]>([]);
  const [bounds, setBounds] = useState<number[]>([]);
  //eslint-disable-next-line
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [styleLoaded, setStyleLoaded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const formatSchedule = (schedule) => {
    return schedule?.map((el: any) => {
      const dayNamesRo = {
        L: "Luni",
        M: "Marți",
        M2: "Miercuri",
        J: "Joi",
        V: "Vineri",
        S: "Sâmbătă",
        D: "Duminică",
      };
      const daysFormatted = el.days
        .map((d: string) => {
          if (d === "M") return dayNamesRo["M"];
          if (d === "L") return dayNamesRo["L"];
          if (d === "J") return dayNamesRo["J"];
          if (d === "V") return dayNamesRo["V"];
          if (d === "S") return dayNamesRo["S"];
          if (d === "D") return dayNamesRo["D"];
          if (d === "Miercuri") return dayNamesRo["M2"];
          return d;
        })
        .join(", ");

      const formatHour = (str: string) => {
        const [hour, minute] = str.split(":");
        return `${parseInt(hour)+1}:${minute}`;
      };

      const hours = el.is24Hour
        ? "NON-STOP"
        : `${formatHour(el.startTime)} - ${formatHour(el.endTime)}`;

      return {
        days: daysFormatted,
        hours,
        available: !el.isIndisponible,
      };
    });
  };

  const doClusters = (clusters) => {
    if (map === null) {
      return false;
    }
    try {
      if (map.getLayer("cluster-count")) {
        map.removeLayer("cluster-count");
      }
      if (map.getLayer("clusters")) {
        map.removeLayer("clusters");
      }
      if (map.getLayer("cluster-casing")) {
        map.removeLayer("cluster-casing");
      }
      if (map.getSource("clusters")) {
        map.removeSource("clusters");
      }
    } catch (e) {
      console.error(e);
    }
    map.addSource("clusters", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: clusters,
      },
    });

    map.addLayer({
      id: "clusters",
      type: "circle",
      source: "clusters",

      paint: {
        "circle-color": COLORS.Background,

        "circle-radius": {
          property: "point_count",
          type: "interval",
          stops: [
            [1, 20],
            [100, 30],
            [750, 40],
          ],
        },
      },
    });

    map.addLayer({
      id: "cluster-casing",
      type: "circle",
      source: "clusters",
      paint: {
        "circle-color": COLORS.Yellow,
        "circle-radius": {
          property: "point_count",
          type: "interval",
          stops: [
            [1, 18],
            [100, 29],
            [750, 39],
          ],
        },
      },
    });

    map.addLayer({
      id: "cluster-count",
      type: "symbol",
      source: "clusters",
      layout: {
        "text-field": "{point_count_abbreviated}",
        "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
        "text-size": 12,
      },
    });
  };

  const doMarkers = useCallback(
    (map, points) => {
      if (map === null) {
        return false;
      }
      const tmpMarkers: mapboxgl.Marker[] = [];
      const usePoints: any = [];
      // console.log("points inside", points);
      points.forEach((el: IpPoint) => {
        if (
          el.longitudine < 90 &&
          el.longitudine > -90 &&
          el.latitudine < 90 &&
          el.latitudine > -90
        ) {
          usePoints.push({
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [el.longitudine, el.latitudine],
            },
            properties: {
              data: el,
            },
          });

          tmpMarkers.push(
            new mapboxgl.Marker({
              element: createCustomMarkerElement(
                el.icon,
                SIZE.max100,
                (data: any) => {
                  setPinInfo(data);
                },
                el
              ),
            }).setLngLat([el.longitudine, el.latitudine])
          ); // Marker location [lng, lat]
        }
        // console.log("usePoints", usePoints);
      });

      setShownMarkers((prevState) => {
        prevState.forEach((el: mapboxgl.Marker) => {
          if (el) {
            el.remove();
          }
        });

        const radius = tmpMarkers.length > 80 ? 100 : 50;
        const zoom = tmpMarkers.length > 80 ? 14 : 8;

        if (map.getZoom() > zoom + 1) {
          tmpMarkers.forEach((el: mapboxgl.Marker) => el.addTo(map));
        }
        const initializedCluster = new Supercluster({
          radius: radius, // Radius for clustering
          maxZoom: zoom, // Maximum zoom level to cluster
          minZoom: 1,
        });

        if (initializedCluster) {
          const bbox = getBounds(map);

          initializedCluster.load(usePoints);

          const clusters = initializedCluster.getClusters(
            bbox,
            Math.floor(map.getZoom())
          );
          //TODO: DOUBLE CHECK IF WE NEED THIS OR NOT
          // clusters.forEach((el: any) => {
          //   if (el.properties.point_count === undefined) {
          //     const extraMarker = new mapboxgl.Marker({
          //       element: createCustomMarkerElement(
          //         mapper[selectedIpCategory].icon,
          //         SIZE.max100,
          //         (data: any) => {
          //           setPinInfo(data);
          //         },
          //         el.properties.data
          //       ),
          //     }).setLngLat(el.geometry.coordinates);
          //     tmpMarkers.push(extraMarker);
          //     extraMarker.addTo(map);
          //   }
          // });

          doClusters(clusters);
        }
        return tmpMarkers;
      });
    },
    //eslint-disable-next-line
    [selectedIpCategory, bounds]
  );

  // LIFE CYCLE

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    mapService.getIpCategories().then((response: any) => {
      const ipCats = response.data.map((el: any) => ({
        nume: el.attributes.nume,
        id: el.id,
      }));
      ipCats.push({
        nume: "EVENIMENTE",
        id: 11,
      });
      setIpCategories(ipCats);

      [response.data[selectedIpCategory]].forEach((cat: any) => {
        if (selectedIpCategory === 10) {
          if (cat) {
          }
        }
      });
    });
    //eslint-disable-next-line
  }, []);

  useEffect(() => {}, [pinInfo]);

  useEffect(() => {
    setIsLoading(true);
    if (mapContainerRef.current) {
      const initializeMap = () => {
        let initializedMap = null;
        try {
          initializedMap = new mapboxgl.Map({
            container: mapContainerRef.current!,
            style: "mapbox://styles/orsonapikey/cl8d64cck00ab14qqzkx2zawi",
            center: [26.1062, 44.4377],
            zoom: 10,
          });
        } catch (err) {
          return initializeMap();
        }
        initializedMap.on("idle", (event) => {
          if (event.target.isStyleLoaded() && !map) {
            setMap(initializedMap);
          }
        });

        initializedMap.on("load", () => {
          setStyleLoaded(true);

          initializedMap.on("wheel", () => {
            const bbox = getBounds(initializedMap);
            setBounds(bbox);
          });

          initializedMap.on("touchend", (event) => {
            const bbox = getBounds(initializedMap);
            setBounds(bbox);
          });

          initializedMap.on("mouseup", (event) => {
            const bbox = getBounds(initializedMap);
            setBounds(bbox);
          });

          setIsLoading(() => false);
        });
      };

      if (!map) initializeMap();
    }
  }, [map]);

  const fetchFilteredEvents = async () => {
    let eventType = "all";
    const filterType = "upcoming";
    if (checkedState.dsuEvents) {
      eventType = "DSU";
    } else if (checkedState.partnerEvents) {
      eventType = "partner";
    }
    console.log("sm", searchTerm, filterType, month, year, eventType);

    const response: any = await newsAndMediaService.getEvents(
      0,
      1000,
      searchTerm,
      filterType,
      month,
      year,
      eventType
    );

    console.log("getMapEvents response", response);
    // console.log("cat", cat);

    const tmp: IpCategorySet = { [selectedIpCategory]: [] };
    tmp[11] = response.data.map((el: any) => {
      console.log("el", el.attributes);
      return {
        id: el.id,
        // icon: mapper[11].icon,
        icon:
          el.attributes.partener !== null
            ? mapper[selectedIpCategory].pastIcon
            : mapper[selectedIpCategory].icon,
        ...el.attributes,
      };
    });
    console.log("getMapEvents response", tmp);
    setIpPoints(() => tmp);
  };

  useEffect(() => {
    // console.log("selectedIpCategory", selectedIpCategory)
    if (selectedIpCategory && ipPoints[selectedIpCategory] === undefined) {
      setIsLoading(true);
      mapService
        .getIpPoints(selectedIpCategory)
        .then((response: any) => {
          const tmp: IpCategorySet = { ...ipPoints };
          if (selectedIpCategory === 11) {
            (async (cat: any) => {
              let eventType = "all";
              const filterType = "month";
              if (checkedState.dsuEvents) {
                eventType = "DSU";
              } else if (checkedState.partnerEvents) {
                eventType = "partner";
              }
              const response: any = await newsAndMediaService.getEvents(
                0,
                1000,
                searchTerm,
                filterType,
                month,
                year,
                eventType
              );
              // console.log("getMapEvents response", response);
              // console.log("cat", cat);

              const tmp: IpCategorySet = { [selectedIpCategory]: [] };
              tmp[cat] = response.data.map((el: any) => {
                console.log("el", el.attributes);
                return {
                  id: el.id,
                  // icon: mapper[11].icon,
                  icon:
                    el.attributes.partener !== null
                      ? mapper[selectedIpCategory].pastIcon
                      : mapper[selectedIpCategory].icon,
                  ...el.attributes,
                };
              });
              setIpPoints(() => tmp);
            })(selectedIpCategory);
            // console.log("TEST");
          } else if (selectedIpCategory === 10) {
            (async (cat: any) => {
              const response: any = await mapService.getDefibrilators();
              const tmp: IpCategorySet = { [selectedIpCategory]: [] };
              tmp[cat] = [
                ...response.On,
                ...response.On112,
                ...response.Off,
                ...response.Off112,
              ].map((el: any) => {
                el.defibrilator.defibrilator_schedules_id = el.defibrilator
                  .defibrilator_schedules_id
                  ? formatSchedule(el.defibrilator.defibrilator_schedules_id)
                  : [];
                return {
                  id: el.id,
                  icon: el.isOn ? defib : defibOff,
                  titlu: el.titlu,
                  website: el.website,
                  adresa: el.adresa,
                  numar_telefon:
                    el.numar_telefon !== ""
                      ? el.numar_telefon
                      : el.defibrilator.phone,
                  latitudine: el.latitudine,
                  longitudine: el.longitudine,
                  defibrilator: el.defibrilator,
                  canCallEmergency: el.defibrilator.canCallEmergency,
                };
              });
              setIpPoints(() => tmp);
            })(selectedIpCategory);
          } else {
            tmp[selectedIpCategory] = response.data.map((el: any) => {
              return {
                id: el.id,
                icon: mapper[selectedIpCategory].icon,
                ...el.attributes,
              };
            });
            // tmp[selectedIpCategory] = [
            //   {
            //     id: "1",
            //     icon: events,
            //     titlu: "Duis aute irure dolor in reprehend in voluptate…",
            //     adresa: "Str. Daci și romani, nr 19, sector 5 București",
            //     numar_telefon: "TEST",
            //     website: "TEST",
            //     schedule: "11 aprilie 2024,16 aprilie 2024",
            //     latitudine: 44.23,
            //     longitudine: 23.23,
            //   },
            // ];
            setIpPoints(() => tmp);
          }
        })
        .catch((err) => console.log(err));
    }
    //eslint-disable-next-line
  }, [selectedIpCategory]);

  useEffect(() => {}, [map, ipCategories, ipPoints]);

  useEffect(() => {
    // console.log("ipPoints", ipPoints);
    if (
      selectedIpCategory &&
      ipPoints[selectedIpCategory] !== undefined &&
      map !== null &&
      map !== undefined
    ) {
      const bounds = map.getBounds();
      const bbox = [
        bounds.getWest(),
        bounds.getSouth(),
        bounds.getEast(),
        bounds.getNorth(),
      ];

      const points = ipPoints[selectedIpCategory].filter((el: IpPoint) => {
        return (
          el.latitudine <= bbox[3] &&
          el.latitudine >= bbox[2] &&
          el.longitudine <= bbox[1] &&
          el.longitudine >= bbox[0]
        );
      });
      // console.log("points test", points);
      doMarkers(map, points);
      setIsLoading(() => false);
    }
    //eslint-disable-next-line
  }, [map, selectedIpCategory, bounds, ipPoints]);

  // EVENT HANDLERS
  const handleResize = () => {
    setScreenWidth(window.innerWidth);
  };

  return styleLoaded !== null ? (
    <>
      <MapContainer ref={mapContainerRef}>
        {!isMobile ? (
          <InterestPointsMenu>
            {ipCategories.map((el: IpCategory, index: number) => {
              return (
                <li key={index}>
                  <ButtonAtom
                    text={t(`components.ipCategories.${mapper[el.id]["name"]}`)}
                    type={
                      el.id === selectedIpCategory
                        ? ButtonType.MapActive
                        : ButtonType.Audio
                    }
                    leftIcon={false}
                    rightIcon={false}
                    onClick={() => {
                      setSelectedIpCategory(el.id);
                      navigate(
                        `/harta/${modRewrite(el.nume.replace(/_/g, "-"))}-${
                          el.id
                        }`
                      );
                    }}
                  />
                </li>
              );
            })}
          </InterestPointsMenu>
        ) : (
          <MapDropdownWrapper>
            <MapDropdownAtom
              options={ipCategories.map((el: any, index) => ({
                label: t(`components.ipCategories.${mapper[el.id]["name"]}`),
                value: el.id,
                key: index,
              }))}
              title={t("map.dropDown.title")}
              selected={selectedIpCategory}
              dropdownName={t(
                `components.ipCategories.${mapper[selectedIpCategory]["name"]}`
              )}
              onConfirm={(values) => {
                setSelectedIpCategory(values[0].value);
              }}
            />
          </MapDropdownWrapper>
        )}
        {pinInfo && (
          <ResponsiveModal
            type={
              selectedIpCategory === 10
                ? ModalType.Defib
                : selectedIpCategory === 11
                ? ModalType.Event
                : ModalType.Info
            }
            id={pinInfo.id}
            onClose={() => {
              setPinInfo(null);
            }}
            isOpen={pinInfo !== null}
            date={
              selectedIpCategory === 11 &&
              formatDate(pinInfo.start_date, pinInfo.end_date, "ro")
            }
            selectedIpCategory={pinInfo?.category}
            title={pinInfo?.titlu || pinInfo?.title}
            canCallEmergency={pinInfo?.canCallEmergency}
            photo={
              pinInfo?.defibrilator?.photo?.url ||
              pinInfo?.photo?.data?.attributes?.url
            }
            phone={pinInfo?.numar_telefon}
            adress={
              pinInfo?.adresa ||
              `${pinInfo.address || ""} , ${pinInfo.city || ""}`
            }
            website={pinInfo?.website}
            long={pinInfo?.longitudine}
            lat={pinInfo?.latitudine}
            schedule={pinInfo?.schedule}
            partener={pinInfo?.partener}
            stats={
              pinInfo.defibrilator &&
              Array.isArray(pinInfo?.defibrilator?.defibrilator_schedules_id)
                ? pinInfo?.defibrilator?.defibrilator_schedules_id.map(
                    (el: any) => el.available
                  )
                : null
            }
          >
            {pinInfo.defibrilator &&
              pinInfo.defibrilator?.defibrilator_schedules_id?.length > 0 &&
              pinInfo.defibrilator?.defibrilator_schedules_id.map((el: any) => (
                <Fragment key={el.id}>
                  <CustomText text={el.days + "\n"} type={TextType.H4bold} />
                  <CustomText text={el.hours} type={TextType.P1} />
                </Fragment>
              ))}
          </ResponsiveModal>
        )}
        {selectedIpCategory === 11 && (
          <EventsGirdle
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            setCheckedState={setCheckedState}
            checkedState={checkedState}
            year={year}
            setYear={setYear}
            month={month}
            setMonth={setMonth}
            fetchFilteredEvents={fetchFilteredEvents}
          />
        )}
      </MapContainer>
      {isLoading && (
        <PreloaderWrapper>
          <Preloader width={100} height={100} />
        </PreloaderWrapper>
      )}
    </>
  ) : (
    <PreloaderWrapper>
      <Preloader width={100} height={100} />
    </PreloaderWrapper>
  );
};

export default MapComponent;
